import { Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';
import {
  Box,
  Flex,
  Heading,
  List,
  Link,
  ListItem,
  Text,
  Icon,
  theme,
  Image,
} from '@everlywell/ui-kit';
import { Star } from '@phosphor-icons/react';
import AnchorLink from 'components/AnchorLink';
import MarkdownLink from 'components/MarkdownLink';
import MeasuredItemsModal, { Item } from 'components/MeasuredItemsModal';
import RichTextWrapper from 'components/RichTextWrapper';
import Markdown from 'markdown-to-jsx';
import React, { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { notEmpty, notEmptyObject } from 'utils/types/utilityTypes';

import DetailsSection from './components/DetailsSection';
import Eyebrow from './components/Eyebrow';
import ListWithIcons, { ListItemProps } from './components/ListWithIcons';

const overrideOptions: Options = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <p>{children}</p>,
  },
};

export type PDPOverviewSectionProps = Queries.pdpSectionFragment & {
  slug?: string;
  product: Queries.pdpProductFragment;
  whatIsMeasuredData: Queries.PDPQuery['whatsMeasured']['nodes'][0];
};

export default function PDPOverviewSection(props: PDPOverviewSectionProps) {
  const { inView, ref } = useInView({ triggerOnce: true });
  // TODO: refactor this logic to not use old models
  const [isFoodListModalOpen, setIsFoodListModalOpen] =
    useState<boolean>(false);

  const averageRating =
    props.product.spreeProduct?.powerReviews?.average_rating;
  const reviewCount = props.product.spreeProduct?.powerReviews?.review_count;
  const hideRating =
    props.product.metadata?.flags?.find(
      (flag) => flag?.metadata?.metadata_id === 'hideRating',
    )?.value === true;

  const whatsMeasuredSection = props.whatIsMeasuredData;

  const formattedListOfItems: Item[] | undefined =
    whatsMeasuredSection?.listOfContentSections?.map((item: any) => ({
      image: item.image?.file?.url || '',
      title: item.title,
      items: item.description?.description?.split('\n') || [],
    }));

  const overviewSubSection = props?.subsections?.find(
    (section: any) => section?.componentName === 'PDPOverviewSubsection',
  ) as Queries.pdpSubsectionsFragment;
  const detailsSubSection = props?.subsections?.find(
    (section: any) => section?.componentName === 'PDPDetailsSubsection',
  ) as Queries.pdpSubsectionsFragment;

  useEffect(() => {
    if (inView) {
      analytics.track({
        event: ANALYTICS.EVENTS.VIEWED_COMPONENT,
        data: {
          label: 'PDP - Overview Section',
          component: 'PDPOverviewSection',
        },
      });
    }
  }, [inView]);

  if (!overviewSubSection || !detailsSubSection) {
    return null;
  }

  const onFoodListModalOpen = () => {
    setIsFoodListModalOpen(true);
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_MODAL,
      data: {
        label: 'PDP - Overview Section',
        component: 'FoodListModal',
      },
    });
  };

  return (
    <>
      <Flex
        ref={ref}
        flexDirection="column"
        padding={[6, 8]}
        paddingBottom={2}
        marginBottom={4}
        borderRadius={12}
        backgroundColor="tints.lightCream"
        data-testid="overviewSection"
      >
        {overviewSubSection?.metadata?.title ? (
          <Eyebrow marginBottom={3}>
            {overviewSubSection.metadata.title}
          </Eyebrow>
        ) : null}
        {!hideRating && (
          <Box
            display={'flex'}
            flexDirection={'row'}
            gap={1}
            marginBottom={4}
            data-testid="starReviews"
          >
            <Icon
              as={Star}
              color={'utility.rating'}
              weight="fill"
              width={theme.fontSizes['2xl']}
              height={theme.fontSizes['2xl']}
            />
            <Text size="lg" fontWeight={'medium'}>
              {averageRating} ·{' '}
            </Text>{' '}
            <Link size="lg">
              <AnchorLink href="#PDPReviewsSection">
                {reviewCount} Reviews
              </AnchorLink>
            </Link>
          </Box>
        )}
        <Heading
          size="sm"
          marginBottom={6}
          as="div"
          data-testid="heroMeasuresText"
        >
          {overviewSubSection.body ? (
            <RichTextWrapper
              contentfulRichText={overviewSubSection.body}
              options={{ overrideOptions }}
            />
          ) : null}
        </Heading>

        <ListWithIcons
          options={(
            overviewSubSection.subsections
              ?.filter(notEmpty)
              .filter(notEmptyObject) ?? []
          )?.reduce((acc, section) => {
            // filter out non-ContentfulSection001 sections
            if (!('isContentfulSection001' in section)) return acc;
            let sectionText = null;
            if (section?.metadata?.description?.description) {
              sectionText = (
                <Markdown
                  options={{
                    overrides: {
                      p: {
                        component: Text,
                        props: {
                          as: 'p',
                          size: 'md',
                        },
                      },
                      a: {
                        component: MarkdownLink,
                        props: {
                          fontSize: 'inherit',
                          slug: props.slug,
                          whatIsMeasuredData: whatsMeasuredSection,
                        },
                      },
                    },
                  }}
                >
                  {section?.metadata?.description?.description}
                </Markdown>
              );
            } else if (section?.body) {
              sectionText = (
                <RichTextWrapper
                  contentfulRichText={section.body}
                  options={{
                    overrideOptions,
                  }}
                />
              );
            }

            const item = {
              icon: (
                <Box
                  backgroundColor={'tints.white'}
                  borderRadius={'full'}
                  minWidth={36}
                  height={36}
                  padding={2}
                >
                  <Image
                    src={section?.images?.[0]?.iconFile?.file?.url ?? ''}
                    alt=""
                  />
                </Box>
              ),
              text: sectionText,
            };
            return [...acc, item];
          }, [] as ListItemProps[])}
        />
      </Flex>
      <Flex
        ref={ref}
        flexDirection="column"
        padding={[6, 8]}
        paddingBottom={[1, 2]}
        borderRadius={12}
        backgroundColor="tints.lightCream"
        data-testid="detailsSection"
      >
        <DetailsSection
          eyebrow={detailsSubSection?.metadata?.title ?? ''}
          items={(detailsSubSection?.subsections ?? []).map((section: any) => ({
            heading: section?.metadata?.title ?? '',
            description: (
              <>
                <Markdown
                  options={{
                    overrides: {
                      b: {
                        component: Text,
                        props: {
                          as: 'b',
                          fontWeight: 'semiBold',
                        },
                      },
                      ul: {
                        component: List,
                        props: {
                          direction: 'column',
                          gap: 1,
                        },
                      },
                      li: {
                        component: ListItem,
                        props: {
                          color: 'tints.darkGrey',
                          listStyleType: 'initial',
                          marginInlineStart: 4,
                        },
                      },
                      p: {
                        component: Text,
                        props: {
                          size: 'lg',
                          color: 'tints.darkGrey',
                          lineHeight: 'base',
                          marginBottom: 4,
                        },
                      },
                      br: {
                        component: Text,
                        props: {
                          as: 'span',
                          display: 'block',
                          marginBottom: 2,
                        },
                      },
                      a: {
                        component: Link,
                        props: {
                          fontSize: 'inherit',
                        },
                      },
                    },
                  }}
                >
                  {section?.metadata?.description?.description ?? ''}
                </Markdown>

                {formattedListOfItems &&
                  formattedListOfItems.length > 0 &&
                  section?.metadata?.title.includes('measured') && (
                    <Box marginBottom={5}>
                      <Link size="md" onClick={onFoodListModalOpen}>
                        {whatsMeasuredSection?.modalButtonText}
                      </Link>

                      <MeasuredItemsModal
                        open={isFoodListModalOpen}
                        openHandler={setIsFoodListModalOpen}
                        listOfItems={formattedListOfItems}
                        modalTitle={whatsMeasuredSection?.modalTitle || ''}
                      />
                    </Box>
                  )}
              </>
            ),
            symptoms: (section?.subsections ?? []).map(
              (subsection: Queries.ContentfulSection001) => ({
                icon: (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    backgroundColor="tints.cream"
                    borderRadius={24}
                    width={30}
                    height={30}
                  >
                    <Image
                      as="img"
                      src={subsection?.images?.[0]?.iconFile?.file?.url ?? ''}
                      alt=""
                      width={20}
                      height={20}
                    />
                  </Box>
                ),
                heading: subsection?.metadata?.title ?? '',
                details: (
                  <Markdown
                    options={{
                      overrides: {
                        ul: {
                          component: List,
                          props: {
                            fontFamily: 'body',
                            direction: 'column',
                            gap: 1,
                          },
                        },
                        li: {
                          component: ListItem,
                          props: {
                            color: 'tints.darkGrey',
                            listStyleType: 'initial',
                            marginInlineStart: 4,
                          },
                        },
                      },
                    }}
                  >
                    {subsection?.metadata?.description?.description ?? ''}
                  </Markdown>
                ),
              }),
            ),
          }))}
        />
      </Flex>
    </>
  );
}
